<template>
  <BaseModal
    v-model="showModal"
    title="Ajustement de stock"
    size="lg"
    :closable="!isSubmitting"
    :close-on-backdrop="!isSubmitting"
    @close="closeModal"
  >
    <template #header>
      <div class="flex items-center gap-3">
        <div class="w-10 h-10 bg-gradient-to-br from-primary to-primary-dark rounded-lg flex items-center justify-center text-white">
          <BaseIcon name="package" :size="20" />
        </div>
        <h2 class="text-lg font-semibold text-gray-900">Ajustement de stock</h2>
      </div>
    </template>

    <div class="space-y-6">
      <!-- Info produit -->
      <div class="flex gap-4 p-4 bg-gray-50 rounded-xl">
        <div class="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 bg-gray-200">
          <img v-if="product?.image_url" :src="product.image_url" :alt="product.name" class="w-full h-full object-cover" />
          <div v-else class="w-full h-full flex items-center justify-center text-gray-400">
            <BaseIcon name="image" :size="24" />
          </div>
        </div>
        <div class="flex-1 min-w-0">
          <h3 class="text-base font-semibold text-gray-900 truncate">{{ product?.name }}</h3>
          <div class="flex items-center gap-2 mt-2">
            <span class="text-sm text-gray-500">Stock actuel:</span>
            <BaseBadge :variant="stockBadgeVariant" size="sm">
              {{ product?.stock_quantity || 0 }}
            </BaseBadge>
          </div>
        </div>
      </div>

      <form @submit.prevent="handleSubmit" class="space-y-5">
        <!-- Mode d'ajustement -->
        <div>
          <label class="block text-sm font-semibold text-gray-700 mb-2">Mode d'ajustement</label>
          <div class="flex gap-2 p-1 bg-gray-100 rounded-lg">
            <button
              type="button"
              :class="[
                'flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-semibold transition-all',
                adjustmentMode === 'absolute'
                  ? 'bg-white text-primary shadow-sm'
                  : 'text-gray-500 hover:bg-white/50'
              ]"
              @click="adjustmentMode = 'absolute'"
            >
              <BaseIcon name="hash" :size="16" />
              <span>Définir quantité</span>
            </button>
            <button
              type="button"
              :class="[
                'flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-semibold transition-all',
                adjustmentMode === 'relative'
                  ? 'bg-white text-primary shadow-sm'
                  : 'text-gray-500 hover:bg-white/50'
              ]"
              @click="adjustmentMode = 'relative'"
            >
              <BaseIcon name="plus" :size="16" />
              <span>Ajustement relatif</span>
            </button>
          </div>
        </div>

        <!-- Type d'opération -->
        <div>
          <label class="block text-sm font-semibold text-gray-700 mb-2">
            Type d'opération <span class="text-red-500">*</span>
          </label>
          <div class="grid grid-cols-3 md:grid-cols-5 gap-2">
            <button
              v-for="type in adjustmentTypes"
              :key="type.id"
              type="button"
              :class="[
                'flex flex-col items-center gap-1.5 py-3 px-2 rounded-lg border-2 transition-all text-xs font-semibold',
                form.type === type.id
                  ? type.activeClasses
                  : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300 hover:bg-gray-50'
              ]"
              @click="form.type = type.id"
            >
              <BaseIcon :name="type.icon" :size="18" />
              <span>{{ type.label }}</span>
            </button>
          </div>
          <span v-if="errors.type" class="text-xs text-red-500 mt-1">{{ errors.type }}</span>
        </div>

        <!-- Quantité -->
        <div>
          <label class="block text-sm font-semibold text-gray-700 mb-2">
            {{ adjustmentMode === 'absolute' ? 'Quantité finale' : 'Quantité à ajuster' }}
            <span class="text-red-500">*</span>
          </label>

          <!-- Mode relatif -->
          <div v-if="adjustmentMode === 'relative'" class="flex items-center max-w-[160px]">
            <button
              type="button"
              class="w-10 h-10 flex items-center justify-center border border-gray-200 rounded-l-lg bg-gray-50 text-gray-600 hover:bg-gray-100 disabled:opacity-50"
              :disabled="form.quantity <= 1"
              @click="decrementQty"
            >
              <BaseIcon name="minus" :size="16" />
            </button>
            <input
              type="number"
              v-model.number="form.quantity"
              min="1"
              class="w-20 h-10 border-y border-gray-200 text-center text-base font-semibold text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary/20"
              :class="{ 'border-red-500': errors.quantity }"
            />
            <button
              type="button"
              class="w-10 h-10 flex items-center justify-center border border-gray-200 rounded-r-lg bg-gray-50 text-gray-600 hover:bg-gray-100"
              @click="incrementQty"
            >
              <BaseIcon name="plus" :size="16" />
            </button>
          </div>

          <!-- Mode absolu -->
          <div v-else class="space-y-3">
            <div class="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
              <span class="text-sm text-gray-500 font-medium">Stock actuel:</span>
              <span class="text-base font-bold text-gray-900">{{ product?.stock_quantity || 0 }}</span>
            </div>
            <input
              type="number"
              v-model.number="form.set_quantity"
              min="0"
              placeholder="Quantité finale souhaitée"
              class="w-full px-4 py-3 text-lg font-semibold text-center border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:border-primary focus:bg-white focus:ring-2 focus:ring-primary/10 transition-all"
              :class="{ 'border-red-500': errors.set_quantity }"
            />
            <div
              :class="[
                'py-2 px-3 rounded-lg text-sm font-semibold text-center',
                quantityDifference > 0 ? 'bg-green-50 text-green-600' :
                quantityDifference < 0 ? 'bg-red-50 text-red-600' :
                'bg-gray-100 text-gray-500'
              ]"
            >
              <template v-if="quantityDifference > 0">+{{ quantityDifference }} (augmentation)</template>
              <template v-else-if="quantityDifference < 0">{{ quantityDifference }} (réduction)</template>
              <template v-else>Aucun changement</template>
            </div>
          </div>

          <span v-if="errors.quantity || errors.set_quantity" class="text-xs text-red-500 mt-1 block">
            {{ errors.quantity || errors.set_quantity }}
          </span>
        </div>

        <!-- Prix unitaire (optionnel, pour achat) -->
        <BaseInput
          v-if="form.type === 'purchase'"
          v-model.number="form.unit_price"
          type="number"
          label="Prix unitaire d'achat"
          placeholder="Prix d'achat par unité"
        />

        <!-- Référence -->
        <BaseInput
          v-model="form.reference"
          label="Référence"
          placeholder="Ex: BON-2024-001, INV-123..."
          maxlength="100"
        />

        <!-- Raison -->
        <div>
          <label class="block text-sm font-semibold text-gray-700 mb-2">Raison / Notes</label>
          <textarea
            v-model="form.reason"
            rows="3"
            placeholder="Raison de l'ajustement..."
            maxlength="500"
            class="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:border-primary focus:bg-white focus:ring-2 focus:ring-primary/10 transition-all resize-y text-sm"
          ></textarea>
        </div>

        <!-- Résumé -->
        <div
          :class="[
            'flex items-center gap-3 p-4 rounded-xl border-2',
            summaryClasses
          ]"
        >
          <div
            :class="[
              'w-10 h-10 rounded-lg flex items-center justify-center',
              summaryIconClasses
            ]"
          >
            <BaseIcon :name="summaryIcon" :size="20" />
          </div>
          <div class="flex flex-col gap-1">
            <span class="text-xs text-gray-500">Nouveau stock après ajustement:</span>
            <span class="text-sm font-medium text-gray-900">
              <template v-if="adjustmentMode === 'relative'">
                {{ product?.stock_quantity || 0 }}
                <span :class="isAddition ? 'text-green-600' : 'text-red-600'" class="font-semibold">
                  {{ isAddition ? '+' : '-' }}{{ form.quantity || 0 }}
                </span>
                = <strong>{{ newStockQuantity }}</strong>
              </template>
              <template v-else>
                <strong>{{ form.set_quantity || 0 }}</strong>
                <span v-if="quantityDifference !== 0" class="text-gray-400 text-xs ml-2">
                  ({{ quantityDifference > 0 ? '+' : '' }}{{ quantityDifference }})
                </span>
              </template>
            </span>
          </div>
        </div>

        <!-- Alerte stock insuffisant -->
        <BaseAlert
          v-if="newStockQuantity < 0"
          variant="danger"
          :show="true"
          :message="`Stock insuffisant. Stock actuel: ${product?.stock_quantity || 0}`"
        />
      </form>
    </div>

    <template #footer>
      <div class="flex justify-end gap-3 w-full">
        <BaseButton variant="ghost" :disabled="isSubmitting" @click="closeModal">
          Annuler
        </BaseButton>
        <BaseButton
          variant="primary"
          :loading="isSubmitting"
          :disabled="newStockQuantity < 0"
          @click="handleSubmit"
        >
          Confirmer l'ajustement
        </BaseButton>
      </div>
    </template>
  </BaseModal>
</template>

<script>
import { defineComponent, ref, computed, watch } from 'vue'
import { productsService } from '@/services'
import { useToastStore } from '@/store/modules/toastStore'
import { BaseModal, BaseButton, BaseInput, BaseIcon, BaseBadge, BaseAlert } from '@/components/base'

export default defineComponent({
  name: 'StockAdjustmentModal',
  components: {
    BaseModal,
    BaseButton,
    BaseInput,
    BaseIcon,
    BaseBadge,
    BaseAlert
  },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    product: {
      type: Object,
      default: null
    }
  },
  emits: ['close', 'updated'],
  setup(props, { emit }) {
    const toastStore = useToastStore()

    const adjustmentTypes = [
      { id: 'adjustment', label: 'Ajustement', icon: 'settings', activeClasses: 'border-blue-500 bg-blue-50 text-blue-600' },
      { id: 'purchase', label: 'Achat', icon: 'shopping-cart', activeClasses: 'border-green-500 bg-green-50 text-green-600' },
      { id: 'return', label: 'Retour', icon: 'refresh', activeClasses: 'border-purple-500 bg-purple-50 text-purple-600' },
      { id: 'damage', label: 'Dommage', icon: 'alert-triangle', activeClasses: 'border-red-500 bg-red-50 text-red-600' },
      { id: 'transfer', label: 'Transfert', icon: 'arrow-right', activeClasses: 'border-orange-500 bg-orange-50 text-orange-600' }
    ]

    const adjustmentMode = ref('absolute')

    const form = ref({
      type: 'adjustment',
      quantity: 1,
      set_quantity: 0,
      reason: '',
      reference: '',
      unit_price: null
    })

    const errors = ref({})
    const isSubmitting = ref(false)

    const showModal = computed({
      get: () => props.show,
      set: (value) => {
        if (!value) {
          emit('close')
        }
      }
    })

    const isAddition = computed(() => {
      if (adjustmentMode.value === 'absolute') {
        return (form.value.set_quantity || 0) > (props.product?.stock_quantity || 0)
      }
      return ['purchase', 'return'].includes(form.value.type)
    })

    const isReduction = computed(() => {
      if (adjustmentMode.value === 'absolute') {
        return (form.value.set_quantity || 0) < (props.product?.stock_quantity || 0)
      }
      return !isAddition.value
    })

    const quantityDifference = computed(() => {
      if (adjustmentMode.value === 'absolute') {
        const current = props.product?.stock_quantity || 0
        const target = form.value.set_quantity || 0
        return target - current
      }
      return 0
    })

    const newStockQuantity = computed(() => {
      const currentStock = props.product?.stock_quantity || 0

      if (adjustmentMode.value === 'absolute') {
        return form.value.set_quantity || 0
      }

      const qty = form.value.quantity || 0
      return isAddition.value ? currentStock + qty : currentStock - qty
    })

    const stockBadgeVariant = computed(() => {
      const qty = props.product?.stock_quantity || 0
      if (qty <= 0) return 'danger'
      if (qty <= 5) return 'warning'
      return 'success'
    })

    const summaryClasses = computed(() => {
      if (adjustmentMode.value === 'absolute') {
        if (quantityDifference.value > 0) return 'bg-green-50 border-green-200'
        if (quantityDifference.value < 0) return 'bg-red-50 border-red-200'
        return 'bg-gray-50 border-gray-200'
      }
      return isAddition.value ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
    })

    const summaryIconClasses = computed(() => {
      if (adjustmentMode.value === 'absolute') {
        if (quantityDifference.value > 0) return 'bg-green-100 text-green-600'
        if (quantityDifference.value < 0) return 'bg-red-100 text-red-600'
        return 'bg-gray-100 text-gray-500'
      }
      return isAddition.value ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'
    })

    const summaryIcon = computed(() => {
      if (isAddition.value || (adjustmentMode.value === 'absolute' && quantityDifference.value > 0)) {
        return 'trending-up'
      }
      if (isReduction.value || (adjustmentMode.value === 'absolute' && quantityDifference.value < 0)) {
        return 'trending-down'
      }
      return 'minus'
    })

    watch(() => props.show, (isVisible) => {
      if (isVisible) {
        resetForm()
      }
    })

    watch(() => props.product, () => {
      resetForm()
    })

    const resetForm = () => {
      const currentStock = props.product?.stock_quantity || 0
      form.value = {
        type: 'adjustment',
        quantity: 1,
        set_quantity: currentStock,
        reason: '',
        reference: '',
        unit_price: null
      }
      errors.value = {}
      adjustmentMode.value = 'absolute'
    }

    const incrementQty = () => {
      form.value.quantity++
    }

    const decrementQty = () => {
      if (form.value.quantity > 1) {
        form.value.quantity--
      }
    }

    const validateForm = () => {
      errors.value = {}
      let isValid = true

      if (!form.value.type) {
        errors.value.type = 'Veuillez sélectionner un type d\'opération'
        isValid = false
      }

      if (adjustmentMode.value === 'relative') {
        if (!form.value.quantity || form.value.quantity < 1) {
          errors.value.quantity = 'La quantité doit être supérieure à 0'
          isValid = false
        }

        if (!isAddition.value && newStockQuantity.value < 0) {
          errors.value.quantity = 'Stock insuffisant pour cette opération'
          isValid = false
        }
      } else {
        if (form.value.set_quantity === null || form.value.set_quantity === undefined || form.value.set_quantity < 0) {
          errors.value.set_quantity = 'La quantité finale doit être supérieure ou égale à 0'
          isValid = false
        }
      }

      return isValid
    }

    const handleSubmit = async () => {
      if (!validateForm()) return
      if (!props.product?.id) return

      isSubmitting.value = true

      try {
        const data = {
          type: form.value.type,
          reason: form.value.reason || undefined,
          reference: form.value.reference || undefined
        }

        if (adjustmentMode.value === 'absolute') {
          data.set_quantity = form.value.set_quantity
        } else {
          data.quantity = form.value.quantity
        }

        if (form.value.type === 'purchase' && form.value.unit_price) {
          data.unit_price = form.value.unit_price
        }

        const response = await productsService.adjustStock(props.product.id, data)

        toastStore.addToast({
          type: 'success',
          message: 'Stock ajusté avec succès',
          duration: 3000
        })

        emit('updated', response.data.product)
        emit('close')
      } catch (error) {
        console.error('Erreur lors de l\'ajustement:', error)

        if (error.response?.data?.errors) {
          const backendErrors = error.response.data.errors
          for (const [field, messages] of Object.entries(backendErrors)) {
            errors.value[field] = Array.isArray(messages) ? messages[0] : messages
          }
        } else {
          toastStore.addToast({
            type: 'error',
            message: error.response?.data?.message || 'Erreur lors de l\'ajustement du stock',
            duration: 4000
          })
        }
      } finally {
        isSubmitting.value = false
      }
    }

    const closeModal = () => {
      if (!isSubmitting.value) {
        emit('close')
      }
    }

    return {
      showModal,
      adjustmentMode,
      adjustmentTypes,
      form,
      errors,
      isSubmitting,
      isAddition,
      isReduction,
      quantityDifference,
      newStockQuantity,
      stockBadgeVariant,
      summaryClasses,
      summaryIconClasses,
      summaryIcon,
      incrementQty,
      decrementQty,
      handleSubmit,
      closeModal
    }
  }
})
</script>
